/**
 * WebSocket hook for real-time agent progress updates.
 * Used by RIDER and Husarz agents to push status to frontend.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { ProgressStatus } from '../types/task';

interface UseProgressWebSocketOptions {
  taskId: number;
  enabled?: boolean;
  onStatusUpdate?: (status: ProgressStatus) => void;
}

interface UseProgressWebSocketResult {
  status: ProgressStatus | null;
  isConnected: boolean;
  error: string | null;
  reconnect: () => void;
}

export function useProgressWebSocket({
  taskId,
  enabled = true,
  onStatusUpdate
}: UseProgressWebSocketOptions): UseProgressWebSocketResult {
  const [status, setStatus] = useState<ProgressStatus | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const initialFetchDoneRef = useRef(false);

  // Fetch initial progress via REST API (faster than waiting for WebSocket)
  useEffect(() => {
    if (!enabled || !taskId || initialFetchDoneRef.current) return;

    const fetchInitialProgress = async () => {
      try {
        const response = await fetch(`/api/tasks/${taskId}/progress`);
        if (response.ok) {
          const data = await response.json();
          if (data.has_progress && data.status) {
            const initialStatus: ProgressStatus = {
              task_list: (data.status.task_list || []).map((item: any) => ({
                content: item.content,
                completed: item.completed
              })),
              progress_percent: data.status.progress_percent || 0,
              progress_eta: data.status.progress_eta || null,
              status_description: data.status.status_description || ''
            };
            setStatus(initialStatus);
            onStatusUpdate?.(initialStatus);
            console.log(`Loaded cached progress for task ${taskId}: ${initialStatus.progress_percent}%`);
          }
        }
      } catch (e) {
        console.debug('Could not fetch initial progress:', e);
      }
      initialFetchDoneRef.current = true;
    };

    fetchInitialProgress();
  }, [taskId, enabled, onStatusUpdate]);

  const connect = useCallback(() => {
    if (!enabled || !taskId) return;

    // Build WebSocket URL
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/ws/progress/${taskId}`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log(`Progress WebSocket connected for task ${taskId}`);
        setIsConnected(true);
        setError(null);
        reconnectAttemptsRef.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'progress_update' && data.status) {
            const newStatus: ProgressStatus = {
              task_list: (data.status.task_list || []).map((item: any) => ({
                content: item.content,
                completed: item.completed
              })),
              progress_percent: data.status.progress_percent || 0,
              progress_eta: data.status.progress_eta || null,
              status_description: data.status.status_description || ''
            };

            setStatus(newStatus);
            onStatusUpdate?.(newStatus);
          }
        } catch (e) {
          console.error('Error parsing progress message:', e);
        }
      };

      ws.onerror = (event) => {
        console.error('Progress WebSocket error:', event);
        setError('WebSocket connection error');
      };

      ws.onclose = (event) => {
        console.log(`Progress WebSocket closed for task ${taskId}:`, event.code);
        setIsConnected(false);
        wsRef.current = null;

        // Attempt reconnect with exponential backoff
        if (enabled && reconnectAttemptsRef.current < 5) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
          reconnectAttemptsRef.current += 1;

          reconnectTimeoutRef.current = setTimeout(() => {
            console.log(`Reconnecting progress WebSocket (attempt ${reconnectAttemptsRef.current})...`);
            connect();
          }, delay);
        }
      };
    } catch (e) {
      console.error('Error creating WebSocket:', e);
      setError('Failed to create WebSocket connection');
    }
  }, [taskId, enabled, onStatusUpdate]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setIsConnected(false);
  }, []);

  const reconnect = useCallback(() => {
    disconnect();
    reconnectAttemptsRef.current = 0;
    connect();
  }, [connect, disconnect]);

  useEffect(() => {
    if (enabled) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [taskId, enabled, connect, disconnect]);

  return {
    status,
    isConnected,
    error,
    reconnect
  };
}
